import React, { useContext, useEffect, useMemo, useState, useCallback, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { StatusConfigProps, StatusConfigValue } from './types';
import LibraryTitleInput from '../library-title-input';
import { NewInput } from '../../PIM/common/NewInput';
import FieldSetupPanelContext from './field-setup-panel.context';
import OptionRow, { moveOption, useOptionReorder } from './option-row';
import { NewRadio } from '../../PIM/common/NewRadio';

/**
 * StatusConfig component for configuring status form controls in the system canvas.
 * Allows users to set up status options with custom names and hex color codes.
 * Provides a visual color picker interface for status customization.
 * @param value - Current status configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param showLibraryTitle - Whether to show the library title input section
 */
const StatusConfig: React.FC<StatusConfigProps> = ({ value, onChange, className = '', showLibraryTitle = true, width, onWidthChange, widthError }) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const showWidthRow = onWidthChange != null;
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const fallbackColours: StatusConfigValue['statusColours'] = useMemo(
    () => [
      { statusName: 'Green', statusHexCode: '#24B36B' },
      { statusName: 'Amber', statusHexCode: '#F1B44C' },
      { statusName: 'Red', statusHexCode: '#F46A6A' },
      { statusName: 'Orange', statusHexCode: '#FD7E14' },
      { statusName: 'Purple', statusHexCode: '#6F42C1' },
      { statusName: 'Pink', statusHexCode: '#E83E8C' },
    ],
    [],
  );

  const [localColours, setLocalColours] = useState<StatusConfigValue['statusColours']>(
    value?.statusColours && value.statusColours.length > 0 ? value.statusColours : fallbackColours,
  );
  const [localTitle, setLocalTitle] = useState<string>(value?.title ?? '');
  const [libraryTitle, setLibraryTitle] = useState<string>('');

  useEffect(() => {
    if (value?.statusColours && value.statusColours.length > 0) {
      setLocalColours(value.statusColours);
    }
  }, [value?.statusColours]);

  useEffect(() => {
    if (typeof value?.title === 'string') {
      setLocalTitle(value.title);
    }
  }, [value?.title]);

  const handleHexChange = useCallback(
    (index: number, hex: string) => {
      const next = localColours.map((colourItem, colourIndex) => (colourIndex === index ? { ...colourItem, statusHexCode: hex } : colourItem));
      setLocalColours(next);
      onChange({ ...(value || { statusColours: [], title: '' }), title: localTitle, statusColours: next });
    },
    [localColours, localTitle, value, onChange],
  );

  const handleNameChange = useCallback(
    (index: number, name: string) => {
      const next = localColours.map((colourItem, colourIndex) => (colourIndex === index ? { ...colourItem, statusName: name } : colourItem));
      setLocalColours(next);
      onChange({ ...(value || { statusColours: [], title: '' }), title: localTitle, statusColours: next });
    },
    [localColours, localTitle, value, onChange],
  );

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as StatusConfigValue | undefined;
      if (fetchedConfig) {
        if (fetchedConfig.statusColours && fetchedConfig.statusColours.length > 0) {
          setLocalColours(fetchedConfig.statusColours);
        }
        if (fetchedConfig.title) {
          setLocalTitle(fetchedConfig.title);
        }
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextTitle = e.target.value;
      setLocalTitle(nextTitle);
      onChange({ ...(value || { statusColours: [], title: '' }), title: nextTitle, statusColours: localColours });
    },
    [value, localColours, onChange],
  );

  const currentConfigValue: StatusConfigValue = useMemo(
    () => ({
      title: localTitle,
      statusColours: localColours,
    }),
    [localTitle, localColours],
  );

  /**
   * Stores a new list of status options and reports it to the field.
   * @param next - The updated status options.
   */
  const emitColours = (next: StatusConfigValue['statusColours']) => {
    setLocalColours(next);
    onChange({ ...(value || { statusColours: [], title: '' }), title: localTitle, statusColours: next });
  };

  const statusReorder = useOptionReorder((fromIndex, toIndex) => emitColours(moveOption(localColours, fromIndex, toIndex)));

  /**
   * Makes one status the default, or clears it when it already is.
   * @param index - Row whose default radio was clicked.
   */
  const toggleDefaultStatus = (index: number) => {
    emitColours(localColours.map((colourItem, colourIndex) => ({ ...colourItem, setDefault: colourIndex === index ? !colourItem.setDefault : false })));
  };

  const statusNameRefs = useRef<(HTMLInputElement | null)[]>([]);
  const previousStatusCountRef = useRef(localColours.length);

  useEffect(() => {
    const statusCount = localColours.length;
    const statusWasAdded = statusCount > previousStatusCountRef.current;
    previousStatusCountRef.current = statusCount;
    if (statusWasAdded) statusNameRefs.current[statusCount - 1]?.focus();
  }, [localColours.length]);

  const cardStatusRows = (
    <div className="cm-status-options">
      {localColours.map((stat, keyIn) => (
        <OptionRow
          key={`status_${keyIn}`}
          index={keyIn}
          label={stat.statusName}
          placeholder={t('CUSTOM_MODULES.LIBRARY.CONTROL_CONFIG.STATUS_CONFIG.ADD_COLOR_NAME', { ns: 'common' })}
          inputRef={(element) => {
            statusNameRefs.current[keyIn] = element;
          }}
          onLabelChange={(nextName) => handleNameChange(keyIn, nextName)}
          isLast={keyIn === localColours.length - 1}
          canDelete={localColours.length > 1}
          onAdd={() => emitColours([...localColours, { statusName: '', statusHexCode: NEW_STATUS_HEX }])}
          onDelete={() => emitColours(localColours.filter((_, colourIndex) => colourIndex !== keyIn))}
          isDragOver={statusReorder.dragOverIndex === keyIn}
          onReorderStart={statusReorder.onReorderStart}
          onReorderOver={statusReorder.onReorderOver}
          onReorderDrop={statusReorder.onReorderDrop}
          onReorderEnd={statusReorder.onReorderEnd}
          inputClassName="cm-status-option"
          inputStyle={{ [STATUS_COLOUR_VAR]: stat.statusHexCode } as React.CSSProperties}
          inputStartAdornment={
            <label className="cm-status-option__swatch">
              <input
                type="color"
                className="cm-status-option__picker"
                value={stat.statusHexCode}
                onChange={(event) => handleHexChange(keyIn, event.target.value)}
                aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.STATUS.COLOUR')}
              />
            </label>
          }
          defaultControl={
            <NewRadio
              name="status-preset"
              checked={!!stat.setDefault}
              onClick={() => toggleDefaultStatus(keyIn)}
              onChange={() => {}}
              aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.SET_AS_DEFAULT')}
            />
          }
        />
      ))}
    </div>
  );

  if (isInSetupCard) {
    return setupPanel === 'options' ? cardStatusRows : null;
  }

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && (
        <div className="mb-2">
          <p className="m-0 mb-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.STATUS.TITLE_LABEL')}</p>
          <NewInput className="mb-2" type="text" placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER')} value={localTitle} onChange={handleTitleChange} />
        </div>
      )}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="status"
          contentData={currentConfigValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      <div className="">
        {!showWidthRow && (
          <>
            <p className="m-0 mb-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.STATUS.TITLE_LABEL')}</p>
            <NewInput className="mb-2"
              type="text"
              placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER')}
              value={localTitle}
              onChange={handleTitleChange}
            />
          </>
        )}
        <div className="pt-2">
          <p className="mb-1 f-13">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.STATUS.OPTIONS')}</p>

          {localColours.map((stat, keyIn) => (
            <div key={`status_${keyIn}`} className="d-flex align-items-center mb-2">
              <div className="w-50">
                <NewInput
                  type="text"
                  value={stat?.statusName}
                  placeholder={t('CUSTOM_MODULES.LIBRARY.CONTROL_CONFIG.STATUS_CONFIG.ADD_COLOR_NAME', { ns: 'common' })}
                  onChange={(e) => handleNameChange(keyIn, e.target.value)}
                />
              </div>
              <div className="flex-grow-1 mx-2">
                <div
                  className="w-100"
                  style={{
                    height: 16,
                    borderRadius: 4,
                    backgroundColor: stat?.statusHexCode,
                  }}
                />
              </div>
              <input
                type="color"
                className="p-0 border-0 w-50"
                value={stat.statusHexCode}
                onChange={(e) => handleHexChange(keyIn, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(StatusConfig);

const NEW_STATUS_HEX = '#DCF0EB';

const STATUS_COLOUR_VAR = '--cm-status-colour';
